import { useState, type SyntheticEvent } from 'react'
import {
  PublicFormError,
  startFormSession,
  type FormSession,
  type PublicFormInfo,
} from '@/lib/publicForm'

interface Props {
  form: PublicFormInfo
  onUnlocked: (session: FormSession) => void
  /** Shown when a session expired mid-form rather than on first arrival. */
  reason?: string | undefined
}

export function PasscodeGate({ form, onUnlocked, reason }: Props) {
  const [passcode, setPasscode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()
    if (passcode.trim() === '') {
      setError('Enter the passcode from the event.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      onUnlocked(await startFormSession(form.slug, passcode))
    } catch (err) {
      setError(
        err instanceof PublicFormError ? err.message : 'Something went wrong. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="attendee-card" onSubmit={(e) => void handleSubmit(e)} noValidate>
      {reason !== undefined && <p className="attendee-banner">{reason}</p>}

      <div className="field">
        <label className="field-label" htmlFor="passcode">
          Event passcode
        </label>
        <p className="field-hint" id="passcode-hint">
          The organisers gave this out at the event.
        </p>
        <input
          id="passcode"
          name="passcode"
          type="text"
          value={passcode}
          onChange={(e) => {
            setPasscode(e.target.value)
          }}
          // A shared code read off a card: no password manager, no autocorrect,
          // and no automatic capitalisation of the first letter.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="go"
          aria-describedby="passcode-hint"
          aria-invalid={error !== null}
          required
        />
        {error !== null && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
      </div>

      <button className="attendee-btn" type="submit" disabled={submitting}>
        {submitting ? 'Checking…' : 'Continue'}
      </button>
    </form>
  )
}
